import type * as React from 'react';

import { cn } from '../../styles/lib/utils';
import type { AvatarMember, AvatarUser } from '../../utils';
import { AvatarIcon } from '../AvatarData';
import { Icon } from '../Icon';

type AvatarStackProps = React.ComponentProps<'div'> & {
  users: readonly (AvatarUser | AvatarMember)[];
  /** How many avatars to show before the rest are counted as hidden. */
  max?: number;
  /** Total number of people, when it is larger than `users`. */
  total?: number;
  size?: 'large' | 'small';
};

/** Overlapping avatars for a group of people, announcing each name and how many are hidden. */
function AvatarStack({ users, max = 3, total, size = 'large', className, ...props }: AvatarStackProps) {
  const shown = users.slice(0, max);
  const hiddenCount = Math.max(0, (total ?? users.length) - shown.length);

  return (
    <div data-slot="avatar-stack" className={cn('flex', className)} {...props}>
      {shown.length === 0 ? (
        <span
          className={cn(
            'flex shrink-0 items-center justify-center rounded-full bg-grey-20 text-grey-90',
            size === 'large' ? 'h-7 w-7' : 'h-5 w-5',
          )}
        >
          <Icon icon="person" />
          <span className="sr-only">No known members</span>
        </span>
      ) : (
        shown.map((user, index) => (
          <AvatarIcon
            key={user.email}
            user={user}
            size={size}
            announceName
            className={cn('ring-2 ring-white', index > 0 && (size === 'large' ? '-ml-3.5' : '-ml-2.5'))}
            style={{ zIndex: shown.length - index }}
          />
        ))
      )}
      {hiddenCount > 0 && <span className="sr-only">and {hiddenCount} more</span>}
    </div>
  );
}

export { AvatarStack, type AvatarStackProps };
